import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase-config';
import { SecureLog } from '../utils/SecurityLogger';
import THEME from '../theme';
import { motion, AnimatePresence } from 'framer-motion';

// ======================== ANIMATION VARIANTS ========================

// [ANIMATION] Staggered card entrance — parent container
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
};

// [ANIMATION] Individual card entrance
const cardVariants = {
    hidden: { opacity: 0, y: 16, scale: 0.97 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { type: 'spring', stiffness: 260, damping: 20 }
    },
    exit: {
        opacity: 0, scale: 0.95, y: -8,
        transition: { duration: 0.15 }
    }
};

// [ANIMATION] Dialog entrance
const dialogVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { type: 'spring', damping: 22, stiffness: 300 } },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.15 } }
};

function InstituteSelection() {
    const [institutes, setInstitutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedInstitute, setSelectedInstitute] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const auth = getAuth();

    // [SECURITY] Auth guard — redirect unauthenticated users
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (!user) {
                SecureLog.warn('[InstituteSelection] Unauthenticated access attempt — redirecting to login.');
                navigate('/login');
            }
        });
        return () => unsubscribe();
    }, [auth, navigate]);

    useEffect(() => {
        const fetchInstitutes = async () => {
            try {
                const institutesRef = collection(db, "institutes");
                const q = query(institutesRef, where("isActive", "==", true));
                const querySnapshot = await getDocs(q);
                const institutesData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setInstitutes(institutesData);
            } catch (err) {
                SecureLog.error("Error fetching institutes:", err);
                setError('Failed to load institutes. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchInstitutes();
    }, []);

    const filteredInstitutes = institutes.filter(inst => {
        const q = searchQuery.toLowerCase();
        return (
            (inst.name || '').toLowerCase().includes(q) ||
            (inst.shortName || '').toLowerCase().includes(q) ||
            (inst.code || '').toLowerCase().includes(q) ||
            (inst.place || '').toLowerCase().includes(q)
        );
    });

    const handleSelect = (institute) => {
        setSelectedInstitute(institute);
        setShowConfirmDialog(true);
    };

    const handleConfirm = () => {
        setShowConfirmDialog(false);
        navigate('/profile-setup', { state: { institute: selectedInstitute } });
    };

    const handleLogout = () => {
        signOut(auth).then(() => navigate('/')).catch(SecureLog.error);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: THEME.colors.midnightBlue }}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
                    <p className="text-slate-400 text-sm">Loading institutes...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen relative overflow-hidden"
            style={{ backgroundColor: THEME.colors.midnightBlue, fontFamily: THEME.fonts.body }}
        >
            {/* --- LIVING BACKGROUND --- */}
            <div className="absolute top-0 -left-4 w-72 h-72 bg-amber-500 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>

            {/* --- CONTENT --- */}
            <div className="relative z-10 max-w-2xl mx-auto px-4 py-8 min-h-screen flex flex-col">
                {/* Header with Logout */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 style={{ fontFamily: THEME.fonts.heading }} className="text-2xl md:text-3xl font-bold text-white">
                            Select Your Institute
                        </h1>
                        <p className="text-slate-400 text-sm mt-1">Choose the institute you belong to</p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="text-slate-400 hover:text-red-400 transition-colors p-2 rounded-lg hover:bg-red-500/10"
                        title="Sign Out"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                    </button>
                </div>

                {/* Search Bar */}
                <div className="relative mb-4">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                        <svg className="h-5 w-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <input
                        type="text"
                        placeholder="Search by name, code or location..."
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm placeholder-slate-500 transition-all duration-200 focus:outline-none"
                        style={{
                            backgroundColor: THEME.colors.inputBg || '#0F172A',
                            border: `1px solid ${searchQuery ? THEME.colors.goldAccent + '40' : THEME.colors.borderColor}`,
                            color: 'white',
                            boxShadow: searchQuery ? '0 0 0 2px rgba(245, 158, 11, 0.1)' : 'none'
                        }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>

                {/* [FEATURE] Institute Count Badge */}
                <div className="flex items-center justify-between mb-4 px-1">
                    <p className="text-slate-500 text-xs font-medium">
                        {searchQuery
                            ? `Showing ${filteredInstitutes.length} of ${institutes.length} institutes`
                            : `${institutes.length} institutes available`
                        }
                    </p>
                    {searchQuery && filteredInstitutes.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                            {filteredInstitutes.length} match{filteredInstitutes.length !== 1 ? 'es' : ''}
                        </span>
                    )}
                </div>

                {error && (
                    <div className="bg-red-900/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-xl mb-6 text-sm flex items-center">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span>{error}</span>
                    </div>
                )}

                {/* [ANIMATION] Institute List with staggered animation + AnimatePresence for search filtering */}
                <div className="flex-1 pb-4 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                    <AnimatePresence mode="popLayout">
                        {filteredInstitutes.length > 0 ? (
                            <motion.div
                                key="list"
                                className="space-y-3"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {filteredInstitutes.map((institute) => (
                                    <motion.button
                                        key={institute.id}
                                        variants={cardVariants}
                                        layout
                                        exit="exit"
                                        onClick={() => handleSelect(institute)}
                                        className="w-full text-left p-5 rounded-2xl border border-slate-700/50 transition-all duration-200 hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/5 group"
                                        style={{ backgroundColor: THEME.colors.midnightSurface }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                {/* [FEATURE] Institute Logo */}
                                                <div className="w-11 h-11 rounded-xl border border-slate-700 flex items-center justify-center flex-shrink-0 overflow-hidden bg-slate-800/50 group-hover:border-amber-500/30 transition-colors">
                                                    {institute.logoUrl ? (
                                                        <img
                                                            src={institute.logoUrl}
                                                            alt={institute.shortName || institute.name}
                                                            className="w-full h-full object-cover rounded-xl"
                                                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                        />
                                                    ) : null}
                                                    <div className={`items-center justify-center text-amber-500/70 ${institute.logoUrl ? 'hidden' : 'flex'}`}>
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                        </svg>
                                                    </div>
                                                </div>

                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <h3 className="text-white font-semibold text-sm sm:text-base truncate group-hover:text-amber-400 transition-colors">
                                                            {institute.name}
                                                        </h3>
                                                        {institute.shortName && (
                                                            <span className="px-2 py-0.5 rounded-md bg-slate-800 text-slate-400 text-[10px] font-mono border border-slate-700 flex-shrink-0 hidden sm:inline-block">
                                                                {institute.shortName}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {institute.place && (
                                                        <p className="text-slate-500 text-xs flex items-center gap-1">
                                                            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                            {institute.place}
                                                        </p>
                                                    )}
                                                    {institute.code && (
                                                        <p className="text-slate-600 text-[10px] mt-0.5">Code: {institute.code}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="ml-3 text-slate-600 group-hover:text-amber-500 transition-colors flex-shrink-0">
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </motion.button>
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="text-center py-16"
                            >
                                <svg className="w-16 h-16 text-slate-700 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                <p className="text-slate-500 text-sm">
                                    {searchQuery ? 'No institutes match your search.' : 'No institutes available.'}
                                </p>
                                {searchQuery && (
                                    <button onClick={() => setSearchQuery('')} className="mt-3 text-amber-400 text-xs font-medium hover:text-amber-300 transition-colors">
                                        Clear search
                                    </button>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* --- CONFIRMATION DIALOG --- */}
            <AnimatePresence>
                {showConfirmDialog && selectedInstitute && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    >
                        <motion.div
                            variants={dialogVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="rounded-2xl shadow-2xl p-8 w-full max-w-md border border-slate-700"
                            style={{ backgroundColor: THEME.colors.midnightSurface }}
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-amber-500/10 rounded-full text-amber-500">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                </div>
                                <h3 style={{ fontFamily: THEME.fonts.heading }} className="text-xl font-bold text-white">Confirm Institute</h3>
                            </div>

                            <p className="text-slate-400 text-sm mb-6">
                                You are about to set up your profile under:
                            </p>

                            <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800 mb-6 space-y-2">
                                <div className="flex items-center gap-3">
                                    {selectedInstitute.logoUrl && (
                                        <img src={selectedInstitute.logoUrl} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-700" />
                                    )}
                                    <p className="text-white font-semibold text-lg">{selectedInstitute.name}</p>
                                </div>
                                {selectedInstitute.shortName && (
                                    <p className="text-slate-400 text-sm">Short Name: <span className="text-slate-300">{selectedInstitute.shortName}</span></p>
                                )}
                                {selectedInstitute.place && (
                                    <p className="text-slate-400 text-sm">Location: <span className="text-slate-300">{selectedInstitute.place}</span></p>
                                )}
                            </div>

                            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 mb-8">
                                <p className="text-xs text-amber-400/80 text-center">
                                    ⚠️ This cannot be changed later without contacting support.
                                </p>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowConfirmDialog(false)}
                                    className="px-5 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    className="px-6 py-2.5 rounded-lg font-bold text-slate-900 text-sm shadow-md transition-transform active:scale-95"
                                    style={{ background: THEME.colors.goldAccent }}
                                >
                                    Confirm & Continue
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default InstituteSelection;
